import React from 'react';

export interface ClientEmailSource {
  email?: string | null;
  additionalEmails?: string[] | null;
}

export const getClientEmailOptions = (client?: ClientEmailSource | null) => {
  const candidates = [client?.email, ...(client?.additionalEmails || [])];
  const seen = new Set<string>();

  return candidates
    .map((email) => email?.trim() || '')
    .filter((email) => {
      if (!email) return false;
      const normalized = email.toLowerCase();
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
};

export const getDefaultClientRecipients = (client?: ClientEmailSource | null) => {
  const primary = client?.email?.trim();
  return primary ? [primary] : [];
};

interface ClientRecipientSelectorProps {
  client?: ClientEmailSource | null;
  value: string[];
  onChange: (emails: string[]) => void;
  label?: string;
}

const ClientRecipientSelector: React.FC<ClientRecipientSelectorProps> = ({
  client,
  value,
  onChange,
  label = 'Client recipients',
}) => {
  const emails = getClientEmailOptions(client);
  const selected = new Set(value.map((email) => email.toLowerCase()));
  const primary = client?.email?.trim().toLowerCase();

  const toggleEmail = (email: string) => {
    const normalized = email.toLowerCase();
    if (selected.has(normalized)) {
      onChange(value.filter((item) => item.toLowerCase() !== normalized));
      return;
    }
    onChange([...value, email]);
  };

  return (
    <fieldset>
      <legend className="text-sm font-medium text-neutral-700">{label}</legend>
      {emails.length === 0 ? (
        <p className="mt-2 text-sm text-error-600">This client has no saved email addresses.</p>
      ) : (
        <div className="mt-2 divide-y divide-neutral-200 rounded-lg border border-neutral-200 bg-white">
          {emails.map((email) => (
            <label key={email.toLowerCase()} className="flex cursor-pointer items-center gap-3 px-3 py-2.5">
              <input
                type="checkbox"
                checked={selected.has(email.toLowerCase())}
                onChange={() => toggleEmail(email)}
                className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="min-w-0 flex-1 truncate text-sm text-neutral-800">{email}</span>
              {email.toLowerCase() === primary && (
                <span className="text-xs font-medium text-primary-700">Primary</span>
              )}
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
};

export default ClientRecipientSelector;
